import { defineStore } from "pinia";

import type { ErrorObject } from "@vuelidate/core";
import {
  CompanyCatalogApi,
  ItemType,
  type CatalogTrainingDetailSessionItemsGroup,
  type CatalogTrainingDetailSessionItemsIndividual,
  TagsApi,
  TrainingsLmsApi,
} from "@simbelapp/order-sdk";
import { useSessionItemStore } from "../session-item/session-item-store";
import { useCatalogStore } from "../catalog/catalog.store";
import { useTrainingPartnersStore } from "../training-partners/training-partners.store";
import { useUsersHabilitationsStore } from "../users-habilitations/users-habilitations.store";
import { useSessionsStore } from "../sessions/sessions-store";
import { useCategoriesStore } from "../categories/categories.store";
import { useTrainingsStore } from "./trainings.store";
import { TagCategoryEnum } from "~/utils/enums/tags.enums";
import { TrainingNavEnum, TrainingTypeEnum, VisibleStatus } from "~/utils/enums/training.enums";
import type {
  ICreateTraining,
  ISessionItemCreateTraining,
  ITrainingFormTag,
  ITrainingSessionDraft,
  IVisibility,
} from "~/utils/interfaces/trainings-interface";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { SessionItemFormatEnum, SessionItemModalityEnum, SessionItemTypeEnum } from "~/utils/enums/session-items.enums";
import { UserLanguageEnum } from "~/utils/enums/users.enums";
import { useAuthStore } from "~/store/auth/auth.store";
import { usePagination } from "~/composables/paginations/usePagination";
import { useTrainingValidation } from "~/composables/trainings/useTrainings";
import type { ICompanyUsersSDK } from "~/utils/interfaces/users-interfaces";
import { useFormats } from "~/composables/utils/useFormat";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { useFilters } from "~/composables/filters/useFilters";
import { hostsTrainingModal } from "~/utils/helpers/trainings/training-modal-host";

/** Empty session-item draft shared by the initial state and the type/format resets. */
function emptySessionItem(type: SessionItemTypeEnum): ISessionItemCreateTraining {
  return {
    isChecked: false,
    modality: SessionItemModalityEnum.SPECIFIC_DATES,
    priceExclTax: 0,
    maxNbAttendees: 1,
    minNbAttendees: 1,
    type,
    overbookingNotAllowed: false,
  };
}

/** Empty session draft used every time the "add a session" form is (re)opened. */
function emptySessionDraft(): ITrainingSessionDraft {
  return {
    trainingAddress: null,
    sessionItemType: null,
    format: null,
    startDate: null,
    endDate: null,
    designation: null,
    overbookingNotAllowed: false,
    sessionTimeslot: [],
    currentSessionTimeslot: null,
  };
}

/** Groups on the training mean "visible to those groups only"; visible without groups means "visible to all". */
function toVisibility(visible: boolean | null, groups: IVisibility["groups"]): IVisibility {
  if (!visible) return { visible: VisibleStatus.HIDDEN, groups: [] };
  if (groups.length) return { visible: VisibleStatus.GROUPS, groups };

  return { visible: VisibleStatus.ALL, groups: [] };
}

/** Catalog-detail session item -> editable form session item. */
function toFormSessionItem(
  sessionItem: CatalogTrainingDetailSessionItemsIndividual | CatalogTrainingDetailSessionItemsGroup,
): ISessionItemCreateTraining {
  return {
    isChecked: true,
    sessionItemId: sessionItem.sessionItemId,
    type: sessionItem.type,
    modality: sessionItem.modality,
    location: sessionItem.location,
    priceExclTax: sessionItem.priceExclTax,
    maxNbAttendees: sessionItem.maxNbAttendees,
    minNbAttendees: sessionItem.minNbAttendees,
    overbookingNotAllowed: sessionItem.overbookingNotAllowed,
  };
}

export const useCreateTrainingStore = defineStore("createTrainingStore", {
  state: (): ICreateTraining => {
    const authStore = useAuthStore();

    return {
      training: {
        general: {
          trainingPartner: null,
          title: null,
          format: null,
          mainContactEmail: null,
          externalUrl: null,
          internalUrl: null,
          isCompanyTraining: false,
          categories: null,
          skills: null,
          durationHours: null,
          durationDays: null,
          visibility: {
            visible: VisibleStatus.HIDDEN,
            groups: [],
          },
          type: null,
          trainingAdmins: [],
          trainingTags: null,
          isSimbelMaster: false,
          lmsId: null,
          lmsRules: null,
        },
        content: {
          goals: [],
          targetAudience: null,
          program: null,
          language: (authStore?.user?.language?.toLowerCase() as UserLanguageEnum) ?? UserLanguageEnum.FR,
          isHabilitation: false,
          habilitation: null,
          durationYears: null,
        },
        sessionItems: {
          individual: emptySessionItem(SessionItemTypeEnum.INDIVIDUAL),
          group: emptySessionItem(SessionItemTypeEnum.GROUP),
        },
        modalities: {
          noRequest: false,
          isNotBillable: false,
          trainerCpfUrl: null,
          comment: null,
        },
        sessions: {
          sessionDraft: emptySessionDraft(),
          sessionList: [],
          catalogSessionsInPlan: [],
          catalogSessionsNotInPlan: [],
          showAddSession: false,
          showAddTimeslot: false,
          showEditTimeslotModalConfirm: null,
          showDeleteModal: false,
          showWarningDeleteModal: false,
          editTimeslot: null,
          currentModalConfirmIndex: null,
        },
        errors: {},
        disabled: {
          mainContactEmail: false,
        },
      },
      nav: TrainingNavEnum.GENERAL,
      showModal: false,
      showExit: false,
      isEdit: false,
      readonly: false,
      trainingId: null,
    };
  },
  getters: {},
  actions: {
    async openTrainingModal(id?: string) {
      this.$reset();
      const router = useRouter();

      if (id) {
        await router.push({ query: { id } });
        await this.loadTraining(id);
        this.trainingId = id;
        this.isEdit = true;
      }

      const defaultUser = this.isEdit ? null : this.buildDefaultTrainingAdmin();
      if (defaultUser) this.training.general.trainingAdmins.push(defaultUser);

      this.showModal = true;
    },
    /** Connected user, shaped as the référent the form pre-selects on a new training. */
    buildDefaultTrainingAdmin(): ICompanyUsersSDK | null {
      const authStore = useAuthStore();
      const u = authStore.user;

      if (!u) return null;

      let displayValue;

      if (u.firstname && u.lastname) {
        displayValue = `${u.firstname} ${u.lastname}`;
      } else if (u.email) {
        displayValue = `${u.email}`;
      } else {
        displayValue = u.employeeNumber ?? "";
      }

      return {
        userId: u.userId!,
        email: u.email ?? "",
        firstname: u.firstname ?? "",
        lastname: u.lastname ?? "",
        employeeNumber: u.employeeNumber ?? "",
        displayValue,
      };
    },
    resetSessionItem(type: SessionItemTypeEnum) {
      return emptySessionItem(type);
    },
    /** Loads the training into the form model; `false` when the API returned nothing (the form is left untouched). */
    async loadTraining(id: string): Promise<boolean> {
      const feedback = useFeedback();
      const formats = useFormats();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CompanyCatalogApi);

      try {
        const { general, content, sessionItems, modalities } = await apiInstance.getCompanyCatalogTraining({
          trainingId: id,
        });

        this.training.general = {
          trainingPartner: general.trainingPartner,
          title: general.title,
          // CatalogTrainingDetailGeneral.format mirrors SessionItemFormatEnum one for one.
          format: (general.format as SessionItemFormatEnum) ?? null,
          mainContactEmail: general.mainContactEmail,
          externalUrl: general.externalUrl,
          internalUrl: general.internalUrl,
          // The catalog detail only carries the id and the label; the rest of ICategory is
          // cosmetic and only used by the settings grid.
          categories: general.categories
            ? {
                categoryId: general.categories.categoryId,
                name: general.categories.name ?? "",
                priority: 0,
                isDefault: false,
                isDeletable: true,
                isSimbelMaster: false,
              }
            : null,
          skills: null,
          durationHours: general.durationHours
            ? formats.formatTo2Decimals(general.durationHours)
            : general.durationHours,
          durationDays: general.durationDays,
          visibility: toVisibility(general.visibility, general.groups),
          type: general.type as TrainingTypeEnum | null,
          isCompanyTraining: general.isCompanyTraining,
          trainingAdmins: general.trainingAdmins,
          trainingTags: general.trainingTags.map((tag) => ({
            tagId: tag.tagId,
            name: tag.tagName,
            tagFamilyId: tag.tagFamilyId,
            tagFamilyColor: tag.tagFamilyColor,
            deletedAt: tag.deletedAt,
          })),
          isExternalLms: general.isExternalLms,
          isSimbelMaster: general.isSimbelMaster,
          lmsId: general.lmsId,
          lmsRules: null,
        };

        this.training.content = {
          goals: content.goals,
          targetAudience: content.targetAudience,
          program: content.program,
          language: content.language as UserLanguageEnum | null,
          isHabilitation: !!content.habilitation,
          habilitation: content.habilitation
            ? {
                habilitationId: content.habilitation.habilitationId,
                name: content.habilitation.name,
                durationYears: content.habilitation.durationYears ?? 0,
              }
            : null,
          durationYears: content.habilitation?.durationYears ?? null,
        };

        this.training.sessionItems = {
          individual: sessionItems.individual
            ? toFormSessionItem(sessionItems.individual)
            : emptySessionItem(SessionItemTypeEnum.INDIVIDUAL),
          group: sessionItems.group
            ? toFormSessionItem(sessionItems.group)
            : emptySessionItem(SessionItemTypeEnum.GROUP),
        };

        this.training.modalities = {
          noRequest: modalities.noRequest,
          isNotBillable: modalities.isNotBillable,
          trainerCpfUrl: modalities.trainerCpfUrl,
          comment: modalities.comment,
        };

        this.training.errors = {};
        this.training.disabled = { mainContactEmail: !!general.mainContactEmail };
        // A training that is not owned by the company comes from the Simbel catalog: read-only.
        this.readonly = !general.isCompanyTraining;

        this.training.sessions = {
          sessionDraft: null,
          sessionList: [],
          catalogSessionsInPlan: [],
          catalogSessionsNotInPlan: [],
          showAddSession: false,
          showAddTimeslot: false,
          showEditTimeslotModalConfirm: null,
          showDeleteModal: false,
          showWarningDeleteModal: false,
          editTimeslot: null,
          currentModalConfirmIndex: null,
        };

        await this.loadSessions(id);
        await this.fetchTrainingLmsRules(id, general.isExternalLms);

        this.training.sessions.sessionList = this.training.sessions.sessionList ?? [];
        this.training.sessions.sessionDraft = emptySessionDraft();
        return true;
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
        return false;
      }
    },

    async loadSessions(trainingId: string) {
      const sessionsStore = useSessionsStore();

      // The two catalogue lists are the Sessions tab. `sessionList` is deliberately NOT fetched:
      // nothing renders the existing sessions any more, and the save pipeline only ever creates the
      // drafts `addSession()` pushes into it — fetching them back was a third read of the same rows.
      const catalogSessions = await sessionsStore.fetchCatalogSessions(trainingId);

      this.training.sessions.catalogSessionsInPlan = catalogSessions.inPlan;
      this.training.sessions.catalogSessionsNotInPlan = catalogSessions.notInPlan;
    },

    async fetchTrainingLmsRules(trainingId: string, isExternalLms?: boolean) {
      if (!isExternalLms) {
        this.training.general.lmsRules = null;
        return;
      }
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingsLmsApi);
        this.training.general.lmsRules = await apiInstance.getTrainingLmsRules({ trainingId });
      } catch {
        this.training.general.lmsRules = null;
      }
    },

    pushValidationErrors(errors: ErrorObject[]) {
      errors.forEach((err: ErrorObject) => {
        this.training.errors[err.$propertyPath] = err.$message as string;
      });
    },

    selectNewTrainingPartner(partnerName: string) {
      this.training.general.trainingPartner = {
        trainingPartnerId: null,
        name: partnerName,
      };
    },

    selectNewCategory(categoryName: string) {
      const categoryStore = useCategoriesStore();
      const alreadyExist =
        categoryStore.categories &&
        categoryStore.categories.find((cat) => {
          return cat.name === categoryName;
        });
      if (!alreadyExist) {
        const newCategory = {
          categoryId: "",
          name: categoryName,
          priority: 12,
          isDefault: false,
          isDeletable: true,
          isSimbelMaster: false,
        };

        this.training.general.categories = newCategory;
        categoryStore.categories && categoryStore.categories.push(newCategory);
      }
    },

    async createSessionItems(
      sessionItems: {
        individual: ISessionItemCreateTraining;
        group: ISessionItemCreateTraining;
      },
      trainingId: string,
    ) {
      const sessionItemStore = useSessionItemStore();

      const promises = [];

      if (sessionItems?.individual?.isChecked && !sessionItems.individual.sessionItemId) {
        promises.push(
          sessionItemStore.createSessionItem(
            {
              ...sessionItems.individual,
            },
            trainingId,
          ),
        );
      }

      if (sessionItems?.group?.isChecked && !sessionItems.group.sessionItemId) {
        promises.push(
          sessionItemStore.createSessionItem(
            {
              ...sessionItems.group,
            },
            trainingId,
          ),
        );
      }

      return await Promise.all(promises);
    },

    async createTrainingPartner() {
      const { general } = this.training;
      const trainingPartnersStore = useTrainingPartnersStore();

      if (!general.trainingPartner?.trainingPartnerId && general.trainingPartner?.name) {
        /** create tp with name */
        const trainingPartner = await trainingPartnersStore.create({
          name: general.trainingPartner.name,
        });
        general.trainingPartner.trainingPartnerId = trainingPartner?.trainingPartnerId;
      }
    },
    async createHabilitation() {
      const { content } = this.training;
      const userHabilitationsStore = useUsersHabilitationsStore();
      if (content.isHabilitation && content?.habilitation?.name && content.durationYears) {
        /** create or update habilitation */
        const createHabilitation = await userHabilitationsStore.createUpdate({
          name: content.habilitation?.name,
          durationYears: content.durationYears,
        });

        if (createHabilitation?.habilitationId) {
          content.habilitation.habilitationId = createHabilitation?.habilitationId;
        }
      } else if (!content.isHabilitation) {
        /** disable habilitation */
        if (content.habilitation?.habilitationId) content.habilitation.habilitationId = null;
      }
    },
    async createUpdateTraining(trainingId?: string) {
      const { general, modalities, content } = this.training;
      const trainingsStore = useTrainingsStore();

      const categoriesIds = [general.categories?.categoryId];
      const isExternalUrlLmsOwned = !!general.lmsId && !general.isSimbelMaster;

      const createOwnTraining = await trainingsStore.createOwnTraining({
        trainingId,
        title: general.title ?? undefined,
        type: general.type ?? undefined,
        format: general.format,
        ...(general.lmsId && {
          isSimbelMaster: general.isSimbelMaster,
        }),
        partnerId: general.trainingPartner?.trainingPartnerId ?? undefined,
        ...(!isExternalUrlLmsOwned && { externalUrl: general.externalUrl }),
        ...(!general.lmsId && { internalUrl: general.internalUrl }),
        isNotBillable: modalities.isNotBillable,
        comment: modalities.comment,
        categories: (categoriesIds ?? []).filter((id): id is string => id !== null && id !== undefined) ?? [],
        noRequest: modalities.noRequest,
        skills: general.skills?.map((s) => s.skillId),
        language: content.language,
        durationHours: general.durationHours,
        trainingPartnerEmail: general.mainContactEmail ?? null,
        durationDays: general.durationDays ?? undefined,
        goals: content.goals.filter((goal) => goal !== ""),
        targetAudience: content.targetAudience ?? undefined,
        program: content.program ?? undefined,
        ...(content.isHabilitation &&
          content.habilitation && {
            mandatory: content.isHabilitation,
            habilitationId: content.habilitation.habilitationId,
          }),
        ...(!content.isHabilitation && {
          mandatory: !content.isHabilitation,
          habilitationId: null,
        }),
      });

      return general && content && createOwnTraining;
    },
    async updateContactEmail() {
      /** change email contact */
      const { general } = this.training;
      const trainingPartnersStore = useTrainingPartnersStore();

      if (general.trainingPartner?.trainingPartnerId && general.mainContactEmail) {
        const hasEmail = await trainingPartnersStore.findOneTrainingPartner(general.trainingPartner?.trainingPartnerId);

        if (!hasEmail?.mainContactEmail || hasEmail?.mainContactEmail !== general.mainContactEmail) {
          await trainingPartnersStore.updateMainContact({
            partnerId: general.trainingPartner.trainingPartnerId,
            email: general.mainContactEmail,
          });
        }
      }
    },
    /**
     * @do change visibility
     * @params trainingIds: [trainingId], compliance to call changeVisibility Multi/Solo
     * @params visibility as IVisibility interface
     */
    async changeVisibility(trainingId: string, visibility: IVisibility) {
      const catalogStore = useCatalogStore();

      if (!(visibility.visible === VisibleStatus.GROUPS && !visibility.groups.length)) {
        await catalogStore.changeVisibility([trainingId], visibility, false, true, true);
      }
    },
    async addTraining(companyId: string) {
      const feedback = useFeedback();
      const { sessionItems } = this.training;

      await this.createTrainingPartner();

      await this.createHabilitation();

      /* CREATE Training — or write the row that already exists (an AI import holds a draft), never a second one **/
      const trainingResult = await this.createUpdateTraining(this.trainingId ?? undefined);

      if (trainingResult) {
        // Known from here on: a retry after a session-item failure upserts the same row.
        this.trainingId = trainingResult.trainingId;

        /** CREATE SI from trainingId */
        const createSessionItemsResult = await this.createSessionItems(sessionItems, trainingResult.trainingId);

        /** CREATE TRAINING ADMIN */
        const trainingsStore = useTrainingsStore();
        const adminsIds = this.training.general.trainingAdmins?.length
          ? this.training.general.trainingAdmins.map((admin) => admin.userId)
          : [];
        await trainingsStore.updateCreateTrainingAdmin(trainingResult.trainingId, adminsIds);

        /** CHANGE VISIBILITY */
        await this.changeVisibility(trainingResult.trainingId, this.training.general.visibility);

        if (TrainingTypeEnum.EXTERNAL === this.training.general.type) await this.updateContactEmail();

        if (createSessionItemsResult) {
          feedback.success("La formation a bien été créée", "small");

          this.isEdit = true;
          this.nav = 4;

          const router = useRouter();
          /*
           * The query, the list and the filters around the form all belong to the pages that host
           * it themselves: elsewhere `?id=` means something else entirely, and the default
           * pagination callback is whatever page registered it last. The store already knows the
           * row it wrote, so nothing here is needed to keep the form working.
           */
          const hostsForm = hostsTrainingModal(router.currentRoute.value);

          if (hostsForm) {
            // Merged, not replaced: the settings section lives in `?section=`, and losing it sends
            // that page back to its main menu behind the still-open form.
            await router.push({
              query: { ...router.currentRoute.value.query, id: trainingResult.trainingId },
            });
          }
          if (companyId) {
            await this.assignTagsToItems(
              companyId,
              TagCategoryEnum.TRAINING_TAG,
              ItemType.Training,
              this.training.general.trainingTags,
              trainingResult.trainingId,
            );
          }
          await this.loadTraining(trainingResult.trainingId);

          if (hostsForm) {
            const paginate = usePagination();
            await paginate.fetchData();

            const filters = useFilters();
            await filters.fetchFilters("companyCatalog");
          }
        } else {
          feedback.error(`Une erreur est survenue`, "small");
        }
      }
    },

    /** Saves the edited training; `true` when the API accepted it. */
    async editTraining(): Promise<boolean> {
      const feedback = useFeedback();

      /** edit Training */
      await this.validation();
      const { sessionItems, sessions, errors } = this.training;
      if (!Object.keys(errors).length) {
        const router = useRouter();
        // The store knows which row it edits; the route query only backs deep links.
        const trainingId = this.trainingId ?? (router.currentRoute.value.query.id as string | undefined);
        if (trainingId) {
          await this.createTrainingPartner();

          await this.createHabilitation();

          /* CREATE Training **/
          const trainingResult = await this.createUpdateTraining(trainingId as string);

          /** UPDATE SI */
          const sessionItemStore = useSessionItemStore();
          if (sessionItems.group.isChecked) {
            if (sessionItems.group.sessionItemId) {
              const sessionItem = await sessionItemStore.updateTrainingSessionItem(
                sessionItems.group.sessionItemId as string,
                {
                  ...sessionItems.group,
                },
                trainingId as string,
              );

              if (!sessionItem?.available) return false;
              sessionItems.group.sessionItemId = sessionItem.available;
            } else {
              const sessionItem = await sessionItemStore.createSessionItem(
                {
                  ...sessionItems.group,
                },
                trainingId as string,
              );
              if (!sessionItem?.available) {
                feedback.error(`Une erreur est survenue lors de la création du format groupe`, "small");
                return false;
              }
              sessionItems.group.sessionItemId = sessionItem.available;
            }
          } else if (sessionItems.group.sessionItemId) {
            await sessionItemStore.disableTrainingSessionItem(sessionItems.group.sessionItemId as string);
          }

          if (sessionItems.individual.isChecked) {
            if (sessionItems.individual.sessionItemId) {
              const sessionItem = await sessionItemStore.updateTrainingSessionItem(
                sessionItems.individual.sessionItemId as string,
                {
                  ...sessionItems.individual,
                },
                trainingId as string,
              );
              if (!sessionItem?.available) return false;
              sessionItems.individual.sessionItemId = sessionItem.available;
            } else {
              const session = await sessionItemStore.createSessionItem(
                {
                  ...sessionItems.individual,
                },
                trainingId as string,
              );
              if (!session?.available) {
                feedback.error(`Une erreur est survenue lors de la création du format individuel`, "small");
                return false;
              }
              sessionItems.individual.sessionItemId = session.available;
            }
          } else if (sessionItems.individual.sessionItemId) {
            await sessionItemStore.disableTrainingSessionItem(sessionItems.individual.sessionItemId as string);
          }

          await this.updateContactEmail();

          if (sessions.sessionList.length) {
            const sessionStore = useSessionsStore();

            for (const s of sessions.sessionList) {
              if (!s.sessionItemId) {
                if (s.sessionItemType === SessionItemTypeEnum.INDIVIDUAL) {
                  s.sessionItemId = sessionItems.individual.sessionItemId as string;
                } else if (s.sessionItemType === SessionItemTypeEnum.GROUP) {
                  s.sessionItemId = sessionItems.group.sessionItemId as string;
                }
              }
            }

            const newSessions = sessions.sessionList.filter((session) => session.isNewSession);
            const created = await Promise.all(newSessions.map((session) => sessionStore.createSession(session)));
            // A draft Orders refused stays new, so that `addSession` keeps its wizard open on it.
            newSessions.forEach((session, index) => {
              if (created[index]) session.isNewSession = false;
            });
          }
          /** CHANGE VISIBILITY */
          await this.changeVisibility(trainingId as string, this.training.general.visibility);

          /** CREATE TRAINING ADMIN */
          const trainingsStore = useTrainingsStore();
          const adminsIds = this.training.general.trainingAdmins?.length
            ? this.training.general.trainingAdmins.map((admin) => admin.userId)
            : [];
          await trainingsStore.updateCreateTrainingAdmin(trainingId as string, adminsIds);

          if (trainingResult) {
            // Reloading the training would close the add-session wizard on the draft just refused.
            if (sessions.sessionList.some((session) => session.isNewSession)) return false;

            feedback.success("Vos modifications ont bien été sauvegardées", "small");
            await this.loadTraining(trainingId as string);

            const filters = useFilters();
            await filters.fetchFilters("companyCatalog");

            return true;
          } else {
            feedback.error(`Une erreur est survenue`, "small");
          }
        }
      }

      return false;
    },

    async addSession() {
      if (this.nav === TrainingNavEnum.SESSIONS) {
        this.training.errors = {};
        await this.validation();

        if (!Object.keys(this.training.errors).length) {
          if (this.training.sessions.sessionDraft) {
            if (!this.training.sessions.sessionDraft.trainingAddress) {
              this.training.sessions.sessionDraft.trainingAddress =
                this.training.sessions.sessionDraft.sessionItemType === SessionItemTypeEnum.INDIVIDUAL
                  ? (this.training.sessionItems.individual.location as string)
                  : (this.training.sessionItems.group.location as string);
            }

            this.training.sessions.sessionDraft.isNewSession = true;

            // Important otherwise won't update ref
            this.training.sessions.sessionDraft.sessionItemId = undefined;

            this.training.sessions.sessionList.push(this.training.sessions.sessionDraft);
          }

          const draft = this.training.sessions.sessionDraft;
          await this.editTraining();

          // Not created: the wizard stays open on the draft, out of the list so that a retry does not push it twice.
          if (draft?.isNewSession) {
            this.training.sessions.sessionList = this.training.sessions.sessionList.filter(
              (session) => !session.isNewSession,
            );
            return;
          }

          this.training.sessions.showAddSession = false;

          this.training.sessions.sessionDraft = emptySessionDraft();
        }
      }
    },
    async validation() {
      const trainingValidation = useTrainingValidation();
      this.training.errors = {};

      if (this.nav === TrainingNavEnum.GENERAL) await trainingValidation.validateStepGeneral();
      if (this.nav === TrainingNavEnum.CONTENT) await trainingValidation.validateStepContent();
      if (this.nav === TrainingNavEnum.SESSION_ITEM) await trainingValidation.validateStepSessionItem();
      if (this.nav === TrainingNavEnum.SESSIONS) await trainingValidation.validateStepSession();
    },
    openAddSession() {
      this.training.sessions.showAddSession = !this.training.sessions.showAddSession;

      // The timeslot dialogs are store-backed: a dropped draft must not leave one armed for
      // the next opening (Escape closes the modal and its confirm dialog in one go).
      this.training.sessions.showAddTimeslot = false;
      this.training.sessions.editTimeslot = null;
      this.training.sessions.showEditTimeslotModalConfirm = null;
      this.training.sessions.currentModalConfirmIndex = null;
      this.training.sessions.showDeleteModal = false;
      this.training.sessions.showWarningDeleteModal = false;

      this.training.sessions.sessionDraft = emptySessionDraft();

      this.training.errors = {};
    },
    async assignTagsToItems(
      companyId: string,
      tagType: TagCategoryEnum,
      itemType: ItemType,
      tags?: ITrainingFormTag[] | null,
      itemIds?: string[] | string,
    ) {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TagsApi);
      // assign en masse
      if (Array.isArray(itemIds) && itemIds?.length) {
        const assignTagDto: any[] = [];
        for (const id of itemIds) {
          // if training tag = tags = []
          if (Array.isArray(tags) && tags.length) {
            for (const tag of tags) {
              assignTagDto.push({
                itemId: id,
                itemType,
                tagId: tag.tagId,
                category: tagType,
                name: tag?.name,
              });
            }
          } else {
            // remove assign tag
            assignTagDto.push({
              itemId: id,
              itemType,
              tagId: undefined,
              category: tagType,
              name: undefined,
            });
          }
        }
        await apiInstance.assignTagsToItems({
          companyId,
          assignTagDto,
        });
        // simple assign
      } else if (itemIds && !Array.isArray(itemIds)) {
        // if training tag = tags[]
        if (Array.isArray(tags) && tags.length) {
          const assignTagDto: any[] = [];
          for (const tag of tags) {
            assignTagDto.push({
              itemId: itemIds,
              itemType,
              tagId: tag.tagId,
              category: tagType,
              name: tag?.name,
            });
          }
          await apiInstance.assignTagsToItems({
            companyId,
            assignTagDto,
          });
        } else {
          await apiInstance.assignTagsToItems({
            companyId,
            assignTagDto: [
              {
                itemId: itemIds,
                itemType,
                tagId: undefined,
                category: tagType,
                name: undefined,
              },
            ],
          });
        }
      }
    },
  },
});
